// เนื้อเรื่องเฉพาะกลุ่มย่อยของปลาและแมลง

import type { GameEvent } from "../../engine/types";

const F = ["FISH"] as const;
const I = ["INSECT"] as const;

export const FISH_INSECT_SUBGROUP_EVENTS: readonly GameEvent[] = [
  // ---------- ปลาฉลามและกระเบน ----------
  {
    id: "shark_born_ready",
    opening: true,
    archetypes: F,
    stages: ["newborn"],
    requires: { subgroups: ["fish.shark_ray"] },
    once: true,
    weight: 30,
    text: "คุณเกิดมาพร้อมฟันครบปาก แม่ว่ายจากไปทันทีโดยไม่หันกลับมามอง",
    fact: "ลูกฉลามเกิดมาพร้อมเอาตัวรอดได้เองทันที และไม่มีพ่อแม่คอยดูแล",
    choices: [
      { label: "ว่ายไปหาแหล่งน้ำตื้น", outcomes: [{ chance: 1, text: "น้ำตื้นใกล้ฝั่งเต็มไปด้วยปลาเล็ก และฉลามตัวใหญ่ไม่ค่อยเข้ามา", effects: { safety: 15, fullness: 10 } }] },
      {
        label: "ออกทะเลลึกเลย",
        outcomes: [
          { chance: 0.6, text: "คุณว่ายได้อย่างอิสระในทะเลกว้าง", effects: { fullness: 15 } },
          { chance: 0.4, text: "ฉลามตัวใหญ่กว่ามองคุณเป็นอาหาร", effects: { safety: -25 } },
        ],
      },
    ],
  },
  {
    id: "shark_nursery",
    archetypes: F,
    stages: ["larva", "juvenile"],
    requires: { subgroups: ["fish.shark_ray"] },
    weight: 6,
    text: "คุณหลบอยู่ในรากของป่าชายเลน",
    fact: "ฉลามหลายชนิดใช้ป่าชายเลนและน้ำตื้นชายฝั่งเป็นแหล่งอนุบาลลูก เพราะปลอดภัยจากฉลามตัวใหญ่",
    choices: [
      { label: "ล่าปลาเล็กในรากไม้", outcomes: [{ chance: 1, text: "อาหารเยอะและปลอดภัย", effects: { fullness: 30, safety: 5 } }] },
      {
        label: "ออกไปสำรวจนอกป่าชายเลน",
        outcomes: [
          { chance: 0.6, text: "คุณเจอฝูงปลาใหญ่กว่าเดิม", effects: { fullness: 35, safety: -10 } },
          { chance: 0.4, text: "ปลากะรังยักษ์อ้าปากงับคุณ", effects: { health: -25, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "shark_electro",
    archetypes: F,
    stages: ["juvenile", "adult"],
    requires: { subgroups: ["fish.shark_ray"] },
    weight: 7,
    text: "ใต้ผืนทรายมีกระแสไฟฟ้าอ่อนๆ จากหัวใจของสัตว์ที่ซ่อนอยู่",
    fact: "ฉลามและกระเบนรับรู้สนามไฟฟ้าอ่อนๆ จากกล้ามเนื้อของเหยื่อได้ แม้เหยื่อจะซ่อนอยู่ใต้ทราย",
    choices: [
      {
        label: "ขุดลงไปในทราย",
        outcomes: [
          { chance: 0.8, text: "ปลาแบนที่ซ่อนอยู่ไม่มีทางหนี", effects: { fullness: 40 } },
          { chance: 0.2, text: "สิ่งที่ซ่อนอยู่คือปลาหินมีพิษ", effects: { health: -20, cause: "accident" } },
        ],
      },
      { label: "ว่ายผ่านไปล่าที่อื่น", outcomes: [{ chance: 1, text: "คุณไล่ฝูงปลาในน้ำกลางแทน", effects: { fullness: 20 } }] },
    ],
  },
  {
    id: "shark_cleaner",
    archetypes: F,
    stages: ["juvenile", "adult"],
    requires: { subgroups: ["fish.shark_ray"] },
    weight: 3,
    text: "คุณว่ายผ่านแนวปะการังที่มีปลาตัวเล็กลายฟ้าดำรอทำความสะอาดอยู่",
    fact: "ปลาพยาบาลกินปรสิตและเนื้อเยื่อตายบนตัวปลาใหญ่ ปลาใหญ่จึงมาจอดให้ทำความสะอาดโดยไม่กินมัน",
    choices: [
      { label: "ลอยนิ่งให้ปลาพยาบาลทำความสะอาด", outcomes: [{ chance: 1, text: "ปลาตัวเล็กๆ จิกกินปรสิตออกจากเหงือกและผิวคุณ", effects: { health: 20, removeFlags: ["injured"] } }] },
      { label: "ว่ายผ่านไป", outcomes: [{ chance: 1, text: "คุณไม่ได้แวะ" }] },
    ],
  },
  {
    id: "shark_finning",
    archetypes: F,
    stages: ["adult"],
    requires: { subgroups: ["fish.shark_ray"] },
    weight: 1.5,
    threatenedWeight: 2.5,
    text: "สายเบ็ดราวยาวหลายกิโลเมตรถูกปล่อยลงทะเล มีปลาติดเบ็ดดิ้นอยู่เป็นเหยื่อล่อ",
    fact: "ฉลามหลายสิบล้านตัวถูกจับทุกปี ส่วนหนึ่งเพื่อเอาครีบไปทำหูฉลาม",
    choices: [
      {
        label: "งับปลาที่ติดเบ็ด",
        outcomes: [
          { chance: 0.3, text: "คุณกระชากปลาหลุดจากเบ็ดได้โดยไม่ติด", effects: { fullness: 30 } },
          { chance: 0.5, text: "เบ็ดเกี่ยวปากคุณ คุณดิ้นจนสายขาด", effects: { health: -30, cause: "human" } },
          { chance: 0.2, text: "คุณถูกลากขึ้นเรือ", effects: { death: "human" } },
        ],
      },
      { label: "ไม่สนใจ ล่าเอง", outcomes: [{ chance: 1, text: "คุณว่ายห่างจากสายเบ็ด", effects: { fullness: 5 } }] },
    ],
  },

  // ---------- ปลาหนัง ----------
  {
    id: "catfish_barbels",
    archetypes: F,
    stages: ["larva", "juvenile", "adult"],
    requires: { subgroups: ["fish.catfish"] },
    weight: 7,
    text: "น้ำขุ่นจนมองไม่เห็นอะไร แต่หนวดของคุณรับรสได้ว่ามีอาหารอยู่ใกล้ๆ",
    fact: "ปลาหนังมีปุ่มรับรสอยู่ที่หนวดและทั่วผิวหนัง จึงหาอาหารได้แม้ในน้ำขุ่นหรือตอนกลางคืน",
    choices: [
      { label: "ตามรสชาติไปตามพื้นโคลน", outcomes: [{ chance: 1, text: "คุณเจอหอยกับตัวอ่อนแมลงฝังอยู่ในโคลน", effects: { fullness: 35 } }] },
      { label: "รอจนกลางคืน", outcomes: [{ chance: 1, text: "กลางคืนอาหารออกมาเยอะกว่า และนักล่ามองไม่เห็นคุณ", effects: { fullness: 25, safety: 10 } }] },
    ],
  },
  {
    id: "catfish_floodplain",
    archetypes: F,
    stages: ["adult"],
    requires: { subgroups: ["fish.catfish", "fish.carp"], notFlags: ["has_mate"] },
    weight: 6,
    text: "ฤดูน้ำหลากมาถึง น้ำเอ่อล้นเข้าท่วมทุ่งและป่าริมแม่น้ำ",
    fact: "ปลาน้ำจืดในลุ่มน้ำโขงจำนวนมากอพยพเข้าไปวางไข่และหากินในพื้นที่น้ำท่วมช่วงฤดูน้ำหลาก",
    choices: [
      {
        label: "ว่ายเข้าไปในทุ่งน้ำท่วม",
        outcomes: [
          { chance: 0.75, text: "ทุ่งน้ำท่วมอุดมไปด้วยอาหาร และคุณพบคู่ที่นั่น", effects: { addFlags: ["has_mate"], fullness: 15 } },
          { chance: 0.25, text: "ลอบดักปลาของชาวบ้านขวางทางอยู่ คุณมุดหลุดออกมาได้", effects: { health: -15, cause: "human" } },
        ],
      },
      { label: "อยู่ในลำน้ำหลัก", outcomes: [{ chance: 1, text: "ปลอดภัยแต่พลาดฤดูวางไข่", effects: { safety: 5 } }] },
    ],
  },
  {
    id: "catfish_dam",
    archetypes: F,
    stages: ["adult"],
    requires: { subgroups: ["fish.catfish", "fish.carp", "fish.other"], habitat: ["freshwater"] },
    weight: 1.5,
    threatenedWeight: 3,
    text: "เส้นทางที่บรรพบุรุษของคุณใช้ว่ายทวนน้ำขึ้นไปวางไข่ ถูกกำแพงคอนกรีตขวางไว้",
    fact: "เขื่อนกั้นแม่น้ำขวางเส้นทางอพยพวางไข่ของปลาน้ำจืดหลายชนิด รวมถึงปลาบึกในแม่น้ำโขง",
    choices: [
      {
        label: "หาทางผ่านเขื่อน",
        outcomes: [
          { chance: 0.3, text: "คุณเจอทางปลาผ่านที่สร้างไว้ข้างเขื่อน", effects: { fullness: -20 } },
          { chance: 0.6, text: "คุณว่ายวนอยู่หน้าเขื่อนจนหมดแรง", effects: { fullness: -25, health: -15, cause: "human" } },
          { chance: 0.1, text: "กระแสน้ำจากประตูระบายน้ำดูดคุณเข้าไป", effects: { death: "human" } },
        ],
      },
      { label: "วางไข่ในที่ที่ไปถึงได้", outcomes: [{ chance: 1, text: "ที่นี่ไม่ดีเท่าแหล่งเดิม แต่ก็ดีกว่าไม่มี", effects: { fullness: -10 } }] },
    ],
  },

  // ---------- ปลาตะเพียนและพวกพ้อง ----------
  {
    id: "carp_fry_school",
    archetypes: F,
    stages: ["larva", "juvenile"],
    requires: { subgroups: ["fish.carp"] },
    weight: 7,
    text: "คุณว่ายอยู่กลางฝูงลูกปลาตัวเท่ากันนับร้อย",
    choices: [
      { label: "ว่ายตามฝูงไม่ให้หลุด", outcomes: [{ chance: 1, text: "ปลาช่อนพุ่งเข้ามาแต่ฝูงแตกกระจายจนมันงับไม่ถูกตัวไหน", effects: { safety: 15 } }] },
      {
        label: "แยกไปกินตะไคร่ริมตลิ่ง",
        outcomes: [
          { chance: 0.6, text: "อาหารริมตลิ่งเยอะกว่ากลางฝูง", effects: { fullness: 35 } },
          { chance: 0.4, text: "นกกระเต็นโฉบลงมา คุณหนีกลับเข้าฝูงทัน", effects: { fullness: 10, safety: -15 } },
        ],
      },
    ],
  },
  {
    id: "carp_drought_pool",
    archetypes: F,
    stages: ["juvenile", "adult"],
    requires: { subgroups: ["fish.carp", "fish.catfish"] },
    weight: 3,
    text: "น้ำลดลงเรื่อยๆ จนคุณติดอยู่ในแอ่งน้ำเล็กๆ กับปลาอีกหลายตัว",
    choices: [
      {
        label: "ดิ้นข้ามโคลนไปแอ่งข้างๆ",
        outcomes: [
          { chance: 0.5, text: "คุณข้ามไปถึงแอ่งที่ใหญ่กว่า", effects: { health: -10 } },
          { chance: 0.5, text: "โคลนแห้งเกินไป คุณติดอยู่กลางทาง", effects: { health: -30, cause: "disaster" } },
        ],
      },
      {
        label: "รอฝน",
        outcomes: [
          { chance: 0.6, text: "ฝนตกลงมาเติมน้ำทันเวลา", effects: { fullness: 10 } },
          { chance: 0.4, text: "ออกซิเจนในแอ่งเหลือน้อยลงทุกวัน", effects: { health: -25, cause: "disaster" } },
        ],
      },
    ],
  },

  // ---------- ปลากะพงและพวกพ้อง ----------
  {
    id: "perch_guard_nest",
    archetypes: F,
    stages: ["adult"],
    requires: { subgroups: ["fish.perch"], flags: ["parent"] },
    weight: 7,
    text: "ไข่ของคุณเพิ่งฟักเป็นลูกปลาตัวจิ๋ว ปลาตัวอื่นเริ่มว่ายวนเข้ามา",
    fact: "ปลาหลายชนิดในกลุ่มนี้เฝ้ารังและดูแลลูก เช่น ปลากัดตัวผู้ที่เฝ้าไข่ในหวอด และปลาหมอสีที่อมลูกไว้ในปาก",
    choices: [
      { label: "ไล่ทุกตัวที่เข้าใกล้", outcomes: [{ chance: 1, text: "ลูกๆ ของคุณรอดไปได้จำนวนมาก", effects: { fullness: -20, safety: -5, removeFlags: ["parent"] } }] },
      { label: "ปล่อยให้ลูกเอาตัวรอดเอง", outcomes: [{ chance: 1, text: "คุณกลับไปหากินตามปกติ", effects: { fullness: 10, removeFlags: ["parent"] } }] },
    ],
  },
  {
    id: "perch_reef_shelter",
    archetypes: F,
    stages: ["larva", "juvenile"],
    requires: { subgroups: ["fish.perch"] },
    weight: 6,
    text: "คุณเจอที่หลบในซอกหินที่มีปะการังและสาหร่ายขึ้นเต็ม",
    choices: [
      { label: "ยึดซอกนี้เป็นบ้าน", outcomes: [{ chance: 1, text: "คุณมีที่หลบภัยถาวร ออกมากินแพลงก์ตอนแล้วรีบกลับเข้าซอก", effects: { safety: 20, fullness: 10 } }] },
      { label: "ว่ายสำรวจต่อ", outcomes: [{ chance: 1, text: "คุณเจออาหารใหม่ๆ แต่ไม่มีที่หลบ", effects: { fullness: 25, safety: -10 } }] },
    ],
  },
  {
    id: "perch_bleaching",
    archetypes: F,
    stages: ["juvenile", "adult"],
    requires: { subgroups: ["fish.perch", "fish.shark_ray"], habitat: ["sea"] },
    weight: 1.5,
    threatenedWeight: 2,
    text: "น้ำทะเลอุ่นผิดปกติมาหลายสัปดาห์ แนวปะการังรอบตัวกลายเป็นสีขาวซีด",
    fact: "เมื่อน้ำทะเลร้อนเกินไป ปะการังจะขับสาหร่ายที่อาศัยอยู่ในเนื้อเยื่อออก จนเห็นเป็นสีขาว เรียกว่าปะการังฟอกขาว",
    choices: [
      { label: "ย้ายไปหาแนวปะการังที่ลึกกว่า", outcomes: [{ chance: 1, text: "น้ำลึกเย็นกว่า ปะการังยังมีสีอยู่", effects: { fullness: -15, safety: -5 } }] },
      { label: "อยู่ที่เดิม", outcomes: [{ chance: 1, text: "อาหารและที่หลบภัยหายไปพร้อมกับปะการัง", effects: { fullness: -20, safety: -15 } }] },
    ],
  },

  // ---------- ปลาอื่นๆ (ปลาไหล ปลาแซลมอน ปลาแฮร์ริ่ง ฯลฯ) ----------
  {
    id: "fishother_bait_ball",
    archetypes: F,
    stages: ["juvenile", "adult"],
    requires: { subgroups: ["fish.other"] },
    weight: 6,
    text: "โลมาและนกทะเลพุ่งเข้าใส่ฝูงของคุณจากทุกทิศทาง",
    choices: [
      {
        label: "ว่ายเบียดรวมกันเป็นก้อนกลม",
        outcomes: [
          { chance: 0.8, text: "ฝูงหมุนวนเป็นก้อน นักล่าจับได้แค่ตัวที่อยู่ขอบ", effects: { safety: 10 } },
          { chance: 0.2, text: "คุณอยู่ขอบฝูงพอดี", effects: { death: "predator" } },
        ],
      },
      {
        label: "แตกฝูงว่ายหนีลงลึก",
        outcomes: [
          { chance: 0.6, text: "คุณหนีลงไปในความมืดได้สำเร็จ", effects: { fullness: -10 } },
          { chance: 0.4, text: "ว่ายตัวเดียวจึงตกเป็นเป้า คุณโดนกัดแต่ยังรอด", effects: { health: -30, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "fishother_long_migration",
    archetypes: F,
    stages: ["adult"],
    requires: { subgroups: ["fish.other"], notFlags: ["has_mate"] },
    weight: 6,
    text: "ร่างกายของคุณเริ่มเปลี่ยนสี สัญชาตญาณบอกให้ออกเดินทางไกลไปยังแหล่งวางไข่",
    fact: "ปลาบางชนิดอพยพไกลมากเพื่อวางไข่ เช่น ปลาแซลมอนที่ว่ายจากทะเลกลับไปยังลำธารที่เกิด และปลาไหลยุโรปที่ว่ายไปวางไข่ในทะเลซาร์กัสโซ",
    choices: [
      {
        label: "ออกเดินทาง",
        outcomes: [
          { chance: 0.65, text: "หลังเดินทางนับพันกิโลเมตร คุณมาถึงแหล่งวางไข่และพบคู่", effects: { move: "far", addFlags: ["has_mate"], fullness: -25 } },
          { chance: 0.35, text: "การเดินทางยาวไกลทำให้คุณหมดแรงก่อนถึง", effects: { move: "near", fullness: -25, health: -20, cause: "accident" } },
        ],
      },
      { label: "รอปีหน้า", outcomes: [{ chance: 1, text: "คุณหากินสะสมพลังงานต่ออีกปี", effects: { fullness: 20 } }] },
    ],
  },

  // ---------- ด้วง ----------
  {
    id: "beetle_grub",
    archetypes: I,
    stages: ["larva"],
    requires: { subgroups: ["insect.beetle"] },
    weight: 8,
    text: "คุณเป็นหนอนด้วงตัวอ้วนขาว อยู่ในกองขอนไม้ผุใต้ดิน",
    fact: "หนอนของด้วงกว่างกินไม้ผุและเศษใบไม้ ช่วยย่อยสลายอินทรียวัตถุให้กลับเป็นดิน",
    choices: [
      { label: "กินไม้ผุไม่หยุด", outcomes: [{ chance: 1, text: "ตัวของคุณใหญ่ขึ้นเรื่อยๆ", effects: { fullness: 35 } }] },
      {
        label: "มุดลึกลงไปอีก",
        outcomes: [
          { chance: 0.7, text: "ลึกลงไปปลอดภัยจากตัวตุ่นและนก", effects: { safety: 15, fullness: 10 } },
          { chance: 0.3, text: "ดินข้างล่างแข็งเกินไป", effects: { fullness: -5 } },
        ],
      },
    ],
  },
  {
    id: "beetle_horn_fight",
    archetypes: I,
    stages: ["adult"],
    requires: { subgroups: ["insect.beetle"], notFlags: ["has_mate"] },
    weight: 8,
    text: "บนกิ่งไม้ที่มีน้ำเลี้ยงหวานๆ ด้วงตัวผู้อีกตัวยกเขาขึ้นท้าทายคุณ",
    fact: "ด้วงกว่างตัวผู้ใช้เขางัดคู่ต่อสู้ให้ตกจากกิ่ง เพื่อแย่งพื้นที่ที่ตัวเมียมาหากิน ภาคเหนือของไทยมีประเพณีชนด้วงจากพฤติกรรมนี้",
    choices: [
      {
        label: "ยกเขาสู้!",
        outcomes: [
          { chance: 0.55, text: "คุณงัดคู่แข่งตกกิ่งไป ตัวเมียเดินเข้ามาหา", effects: { addFlags: ["has_mate"], fullness: -10 } },
          { chance: 0.45, text: "คุณถูกงัดตกลงมาจากกิ่ง", effects: { health: -15, cause: "accident" } },
        ],
      },
      { label: "ถอยไปหากิ่งอื่น", outcomes: [{ chance: 1, text: "กิ่งนี้น้ำหวานน้อยกว่า แต่คุณไม่ต้องสู้", effects: { fullness: 15 } }] },
    ],
  },
  {
    id: "beetle_collected",
    archetypes: I,
    stages: ["adult"],
    requires: { subgroups: ["insect.beetle"] },
    weight: 1.5,
    text: "มีเด็กๆ ถือไฟฉายเดินส่องตามต้นไม้ คอยจับด้วงตัวใหญ่ๆ",
    choices: [
      {
        label: "แกล้งตาย ปล่อยตัวหล่นลงพื้น",
        outcomes: [
          { chance: 0.7, text: "คุณหล่นหายไปในกองใบไม้ ไม่มีใครหาเจอ", effects: { safety: 5 } },
          { chance: 0.3, text: "เด็กเก็บคุณขึ้นมาดู แล้วปล่อยกลับไป", effects: { safety: -10 } },
        ],
      },
      {
        label: "บินหนี",
        outcomes: [
          { chance: 0.6, text: "คุณบินหนีไปเกาะต้นไม้ที่สูงกว่า", effects: { fullness: -5 } },
          { chance: 0.4, text: "คุณถูกจับไปเลี้ยงเพื่อชนด้วง ชีวิตในป่าของคุณจบลง", effects: { death: "human" } },
        ],
      },
    ],
  },

  // ---------- ผีเสื้อและมอธ ----------
  {
    id: "butterfly_host_plant",
    opening: true,
    archetypes: I,
    stages: ["egg"],
    requires: { subgroups: ["insect.butterfly"] },
    once: true,
    weight: 10,
    text: "แม่วางคุณไว้ใต้ใบของพืชชนิดเดียวที่หนอนของคุณกินได้",
    fact: "ผีเสื้อแต่ละชนิดมักวางไข่บนพืชอาหารเฉพาะ เพราะหนอนของมันกินได้เพียงพืชไม่กี่ชนิด",
    choices: [{ label: "รอฟัก", outcomes: [{ chance: 1, text: "ใบไม้ด้านบนช่วยบังแดดและฝนให้คุณ", effects: { safety: 10, health: 5 } }] }],
  },
  {
    id: "butterfly_caterpillar_defense",
    archetypes: I,
    stages: ["larva"],
    requires: { subgroups: ["insect.butterfly"] },
    weight: 6,
    text: "นกตัวหนึ่งกระโดดไปมาบนกิ่งไม้ใกล้ๆ มองหาหนอนกิน",
    fact: "หนอนผีเสื้อบางชนิดมีลายคล้ายดวงตาขนาดใหญ่ หรือทำตัวเหมือนงู เพื่อหลอกให้นักล่าตกใจ",
    choices: [
      {
        label: "พองตัวโชว์ลายดวงตา",
        outcomes: [
          { chance: 0.7, text: "นกตกใจคิดว่าเป็นงู และบินหนีไป", effects: { safety: 10 } },
          { chance: 0.3, text: "นกตัวนี้ไม่หลงกล", effects: { death: "predator" } },
        ],
      },
      {
        label: "อยู่นิ่งๆ ทำตัวเหมือนขี้นก",
        outcomes: [
          { chance: 0.8, text: "นกมองข้ามคุณไป", effects: { safety: 10 } },
          { chance: 0.2, text: "นกจิกคุณไปครึ่งตัว แต่คุณยังรอด", effects: { health: -30, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "butterfly_puddling",
    archetypes: I,
    stages: ["adult"],
    requires: { subgroups: ["insect.butterfly"] },
    weight: 5,
    text: "ริมลำธารมีผีเสื้อหลายร้อยตัวเกาะอยู่บนดินเปียก",
    fact: "ผีเสื้อหลายชนิดดูดน้ำจากดินเปียกริมลำธารเพื่อเก็บแร่ธาตุ โดยเฉพาะโซเดียม",
    choices: [
      { label: "ลงไปดูดแร่ธาตุ", outcomes: [{ chance: 1, text: "แร่ธาตุทำให้คุณแข็งแรงพร้อมผสมพันธุ์", effects: { health: 15 } }] },
      { label: "บินหาดอกไม้ต่อ", outcomes: [{ chance: 1, text: "คุณได้น้ำหวานแทน", effects: { fullness: 20 } }] },
    ],
  },

  // ---------- แมลงวันและยุง ----------
  {
    id: "fly_wet_larva",
    archetypes: I,
    stages: ["larva"],
    requires: { subgroups: ["insect.fly"], families: ["Culicidae"] },
    weight: 8,
    text: "คุณเป็นตัวอ่อนเล็กๆ ในแอ่งน้ำขังข้างบ้าน",
    fact: "ลูกน้ำยุงลายเติบโตในน้ำขังเล็กๆ เช่น ยางรถเก่าและจานรองกระถาง การคว่ำภาชนะจึงช่วยลดยุงได้",
    choices: [
      { label: "กินเศษอินทรีย์ในน้ำ", outcomes: [{ chance: 1, text: "คุณโตขึ้นอย่างรวดเร็ว", effects: { fullness: 35 } }] },
      {
        label: "ดำลงก้นน้ำ",
        outcomes: [
          { chance: 0.7, text: "ปลาหางนกยูงที่คนปล่อยไว้หาคุณไม่เจอ", effects: { safety: 10 } },
          { chance: 0.3, text: "มีคนมาคว่ำภาชนะ น้ำไหลทิ้งไปหมด", effects: { death: "human" } },
        ],
      },
    ],
  },
  {
    id: "fly_maggot",
    archetypes: I,
    stages: ["larva"],
    requires: { subgroups: ["insect.fly"], notFamilies: ["Culicidae"] },
    weight: 8,
    text: "คุณเป็นหนอนตัวจิ๋วอยู่ในกองผลไม้เน่าและใบไม้ที่ทับถมกัน",
    fact: "หนอนแมลงวันหลายชนิดช่วยย่อยสลายซากพืชซากสัตว์ เป็นส่วนสำคัญของการหมุนเวียนธาตุอาหารในธรรมชาติ",
    choices: [
      { label: "กินไปเรื่อยๆ", outcomes: [{ chance: 1, text: "อาหารไม่มีวันหมด คุณโตเร็วมาก", effects: { fullness: 35 } }] },
      {
        label: "มุดลงไปใต้กอง",
        outcomes: [
          { chance: 0.8, text: "ใต้กองชื้นและปลอดภัยจากนก", effects: { safety: 10, fullness: 15 } },
          { chance: 0.2, text: "ด้วงนักล่าซ่อนอยู่ใต้กองเหมือนกัน", effects: { health: -25, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "fly_short_life",
    opening: true,
    archetypes: I,
    stages: ["adult"],
    requires: { subgroups: ["insect.fly"] },
    once: true,
    weight: 6,
    text: "ชีวิตตัวเต็มวัยของคุณสั้นเพียงไม่กี่สัปดาห์ ทุกวันมีค่า",
    choices: [
      { label: "รีบหาคู่ทันที", outcomes: [{ chance: 1, text: "คุณเจอคู่ในกลุ่มที่บินวนอยู่", effects: { addFlags: ["has_mate"], fullness: -10 } }] },
      { label: "หาอาหารให้อิ่มก่อน", outcomes: [{ chance: 1, text: "คุณได้กินจนแข็งแรง", effects: { fullness: 30 } }] },
    ],
  },
  {
    id: "fly_swatter",
    archetypes: I,
    stages: ["adult"],
    requires: { subgroups: ["insect.fly"], habitat: ["urban"] },
    weight: 4,
    text: "คุณบินเข้ามาในบ้าน มีคนถือไม้ตบยุงหันมาทางคุณ",
    choices: [
      {
        label: "บินซิกแซก",
        outcomes: [
          { chance: 0.7, text: "ตาของคุณเห็นการเคลื่อนไหวได้เร็วมาก คุณหลบได้ทุกครั้ง", effects: { safety: 5 } },
          { chance: 0.3, text: "เปรี๊ยะ!", effects: { death: "human" } },
        ],
      },
      { label: "บินออกทางหน้าต่าง", outcomes: [{ chance: 1, text: "คุณออกไปข้างนอกอย่างปลอดภัย", effects: { fullness: -5 } }] },
    ],
  },

  // ---------- ผึ้งและต่อ (ชนิดที่อยู่ตัวเดียว) ----------
  {
    id: "beewasp_cell",
    archetypes: I,
    stages: ["larva"],
    requires: { subgroups: ["insect.bee_wasp"] },
    weight: 8,
    text: "คุณอยู่ในห้องเล็กๆ ที่แม่สร้างไว้ มีอาหารเตรียมไว้ให้แล้ว",
    fact: "ผึ้งและต่อที่อยู่ตัวเดียวหลายชนิด สร้างห้องเล็กๆ ให้ลูกแต่ละตัว และเก็บอาหารไว้ให้ก่อนปิดห้อง",
    choices: [{ label: "กินอาหารที่แม่เตรียมไว้", outcomes: [{ chance: 1, text: "ห้องปิดสนิท อาหารพอสำหรับทั้งช่วงวัยหนอน", effects: { fullness: 35, safety: 10 } }] }],
  },
  {
    id: "beewasp_build",
    archetypes: I,
    stages: ["adult"],
    requires: { subgroups: ["insect.bee_wasp"], flags: ["has_mate"] },
    weight: 8,
    text: "ถึงเวลาสร้างรังให้ลูก",
    choices: [
      { label: "สร้างห้องดินเหนียวทีละห้อง", outcomes: [{ chance: 1, text: "รังดินของคุณแข็งแรงและมีห้องหลายห้อง", effects: { fullness: -15, safety: 5 } }] },
      { label: "ใช้โพรงในลำไม้ไผ่", outcomes: [{ chance: 1, text: "ประหยัดแรงได้มาก", effects: { fullness: -5 } }] },
    ],
  },
  {
    id: "beewasp_sting",
    archetypes: I,
    stages: ["adult"],
    requires: { subgroups: ["insect.bee_wasp"] },
    weight: 4,
    text: "นกจับแมลงตัวหนึ่งบินพุ่งตรงมาหาคุณ",
    choices: [
      {
        label: "ต่อยด้วยเหล็กไน",
        outcomes: [
          { chance: 0.6, text: "นกสะดุ้งและคายคุณทิ้ง", effects: { health: -10, cause: "predator" } },
          { chance: 0.4, text: "นกตัวนี้รู้วิธีกำจัดเหล็กไนก่อนกิน", effects: { death: "predator" } },
        ],
      },
      { label: "บินมุดเข้าพุ่มไม้", outcomes: [{ chance: 1, text: "คุณหลบเข้าพุ่มไม้หนาทึบได้ทัน", effects: { safety: 5 } }] },
    ],
  },

  // ---------- มวนและจักจั่น ----------
  {
    id: "bug_cicada_underground",
    archetypes: I,
    stages: ["nymph"],
    requires: { subgroups: ["insect.bug"], families: ["Cicadidae"] },
    weight: 6,
    text: "คุณอยู่ใต้ดินมืดๆ ดูดน้ำเลี้ยงจากรากต้นไม้",
    fact: "ตัวอ่อนจักจั่นอาศัยอยู่ใต้ดินหลายปี บางชนิดในอเมริกาเหนืออยู่ใต้ดินนานถึง 17 ปีก่อนจะขึ้นมาลอกคราบ",
    choices: [
      { label: "รออยู่ใต้ดินต่อไป", outcomes: [{ chance: 1, text: "ปีแล้วปีเล่าผ่านไปอย่างเงียบสงบ", effects: { fullness: 25, safety: 15 } }] },
      {
        label: "ขุดขึ้นไปใกล้ผิวดิน",
        outcomes: [
          { chance: 0.6, text: "รากใกล้ผิวดินน้ำเลี้ยงเยอะกว่า", effects: { fullness: 35 } },
          { chance: 0.4, text: "ตัวตุ่นขุดมาเจอคุณ", effects: { health: -25, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "bug_stink",
    archetypes: I,
    stages: ["nymph", "adult"],
    requires: { subgroups: ["insect.bug"] },
    weight: 5,
    text: "จิ้งจกตัวหนึ่งย่องเข้ามาข้างหลังคุณ",
    choices: [
      {
        label: "ปล่อยกลิ่นเหม็น",
        outcomes: [
          { chance: 0.8, text: "จิ้งจกถอยกรูด กลิ่นของคุณแย่เกินกว่าจะกิน", effects: { safety: 10 } },
          { chance: 0.2, text: "จิ้งจกตัวนี้ไม่สนกลิ่น", effects: { death: "predator" } },
        ],
      },
      { label: "บินหนี", outcomes: [{ chance: 1, text: "คุณบินไปเกาะใบไม้อีกต้น", effects: { fullness: -5 } }] },
    ],
  },
  {
    id: "bug_ants",
    archetypes: I,
    stages: ["nymph", "adult"],
    requires: { subgroups: ["insect.bug"], families: ["Aphididae", "Coccidae", "Membracidae", "Pseudococcidae"] },
    weight: 3,
    text: "มดกลุ่มหนึ่งเดินมาล้อมรอบคุณ แต่ไม่ได้ทำร้าย",
    fact: "เพลี้ยหลายชนิดกับมดช่วยเหลือกัน มดได้กินน้ำหวานที่เพลี้ยขับออกมา และคอยไล่ศัตรูให้เพลี้ย",
    choices: [
      { label: "ให้น้ำหวานกับมด", outcomes: [{ chance: 1, text: "มดคอยไล่เต่าทองที่จะมากินคุณออกไป", effects: { safety: 20, fullness: -5 } }] },
      { label: "เดินหนี", outcomes: [{ chance: 1, text: "คุณไม่มีใครคุ้มกันแล้ว", effects: { safety: -10 } }] },
    ],
  },
  {
    id: "bug_sing",
    archetypes: I,
    stages: ["adult"],
    requires: { subgroups: ["insect.bug"], families: ["Cicadidae"], notFlags: ["has_mate"] },
    weight: 6,
    text: "ฤดูร้อนมาถึง เสียงร้องดังไปทั่วป่า",
    choices: [
      {
        label: "ร้องให้ดังที่สุด",
        outcomes: [
          { chance: 0.65, text: "เสียงของคุณดึงดูดคู่ได้", effects: { addFlags: ["has_mate"] } },
          { chance: 0.35, text: "เสียงของคุณดึงดูดนกแทน คุณหนีทัน", effects: { safety: -15 } },
        ],
      },
      { label: "ฟังเสียงตัวอื่นแล้วบินไปหา", outcomes: [{ chance: 1, text: "คุณบินไปเจอกลุ่มที่กำลังร้องอยู่", effects: { addFlags: ["has_mate"], fullness: -10 } }] },
    ],
  },

  // ---------- ตั๊กแตนและจิ้งหรีด ----------
  {
    id: "grasshopper_camouflage",
    archetypes: I,
    stages: ["nymph", "adult"],
    requires: { subgroups: ["insect.grasshopper"] },
    weight: 6,
    text: "นกตัวหนึ่งเดินหาอาหารอยู่ในพงหญ้าที่คุณเกาะอยู่",
    choices: [
      {
        label: "เกาะนิ่ง สีตัวกลืนกับใบหญ้า",
        outcomes: [
          { chance: 0.8, text: "นกเดินผ่านคุณไปทั้งที่ห่างกันแค่คืบ", effects: { safety: 10 } },
          { chance: 0.2, text: "นกเห็นคุณขยับหนวด", effects: { health: -25, cause: "predator" } },
        ],
      },
      {
        label: "ดีดตัวกระโดดหนี",
        outcomes: [
          { chance: 0.7, text: "ขาหลังพาคุณกระโดดไปไกลหลายสิบเท่าของความยาวตัว", effects: { fullness: -5 } },
          { chance: 0.3, text: "นกงับคุณกลางอากาศ", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "grasshopper_chirp",
    archetypes: I,
    stages: ["adult"],
    requires: { subgroups: ["insect.grasshopper"], notFlags: ["has_mate"] },
    weight: 7,
    text: "ค่ำแล้ว คุณได้ยินเสียงร้องของตัวอื่นแว่วมาจากพงหญ้า",
    fact: "จิ้งหรีดตัวผู้ทำเสียงโดยการถูปีกคู่หน้าเข้าหากัน และ \"หู\" ของจิ้งหรีดอยู่ที่ขาคู่หน้า",
    choices: [
      { label: "ถูปีกร้องตอบ", outcomes: [{ chance: 1, text: "เสียงเพลงของคุณนำคู่มาหา", effects: { addFlags: ["has_mate"], safety: -5 } }] },
      { label: "เดินตามเสียงไป", outcomes: [{ chance: 1, text: "คุณเจอตัวที่ร้องเพลงไพเราะ", effects: { addFlags: ["has_mate"], fullness: -5 } }] },
    ],
  },
  {
    id: "grasshopper_swarm",
    archetypes: I,
    stages: ["adult"],
    requires: { subgroups: ["insect.grasshopper"] },
    weight: 3,
    text: "ตั๊กแตนรอบตัวเพิ่มจำนวนขึ้นเรื่อยๆ จนรวมกันเป็นฝูงมหึมา",
    fact: "ตั๊กแตนบางชนิดเปลี่ยนพฤติกรรมเมื่ออยู่กันหนาแน่น จากอยู่ตัวเดียวกลายเป็นฝูงใหญ่ที่บินไปกินพืชได้ไกลหลายร้อยกิโลเมตร",
    choices: [
      {
        label: "บินไปกับฝูง",
        outcomes: [
          { chance: 0.6, text: "ฝูงพาคุณไปถึงทุ่งข้าวเขียวขจี", effects: { move: "far", fullness: 40, safety: 10 } },
          { chance: 0.4, text: "เครื่องบินพ่นยาฆ่าแมลงลงมาใส่ฝูง", effects: { health: -35, cause: "human" } },
        ],
      },
      { label: "อยู่ที่เดิม", outcomes: [{ chance: 1, text: "พืชแถวนี้ถูกกินจนเกลี้ยงแล้ว", effects: { fullness: -15 } }] },
    ],
  },
  {
    id: "grasshopper_caught",
    archetypes: I,
    stages: ["adult"],
    requires: { subgroups: ["insect.grasshopper"], habitat: ["grassland", "urban"] },
    weight: 1.5,
    text: "แสงไฟนีออนดวงใหญ่ถูกเปิดกลางทุ่งตอนกลางคืน ข้างใต้มีแผ่นพลาสติกขึงไว้",
    choices: [
      { label: "ไม่สนใจแสง", outcomes: [{ chance: 1, text: "คุณซ่อนตัวอยู่ในพงหญ้าต่อ", effects: { fullness: -5 } }] },
      {
        label: "บินเข้าหาแสง",
        outcomes: [
          { chance: 0.4, text: "คุณบินวนแล้วก็บินกลับเข้าทุ่ง" },
          { chance: 0.6, text: "คุณร่วงลงไปในแผ่นพลาสติก และถูกเก็บไปทำอาหาร", effects: { death: "human" } },
        ],
      },
    ],
  },

  // ---------- แมลงปอ ----------
  {
    id: "dragonfly_nymph_hunt",
    archetypes: I,
    stages: ["nymph"],
    requires: { subgroups: ["insect.dragonfly"] },
    weight: 8,
    text: "คุณซุ่มอยู่ที่ก้นบ่อ ลูกอ๊อดตัวหนึ่งว่ายผ่านมา",
    fact: "ตัวอ่อนแมลงปออาศัยอยู่ในน้ำ และมีริมฝีปากล่างที่ยืดพุ่งออกไปจับเหยื่อได้อย่างรวดเร็ว",
    choices: [
      {
        label: "ยืดริมฝีปากพุ่งจับ",
        outcomes: [
          { chance: 0.75, text: "ฉับ! จับได้ในเสี้ยววินาที", effects: { fullness: 40 } },
          { chance: 0.25, text: "ลูกอ๊อดสะบัดหางหนีไป", effects: { fullness: -5 } },
        ],
      },
      { label: "รอเหยื่อตัวเล็กกว่า", outcomes: [{ chance: 1, text: "ลูกน้ำยุงหลายตัวว่ายเข้ามาใกล้", effects: { fullness: 25 } }] },
    ],
  },
  {
    id: "dragonfly_polluted",
    archetypes: I,
    stages: ["nymph"],
    requires: { subgroups: ["insect.dragonfly"] },
    weight: 2,
    threatenedWeight: 2,
    text: "น้ำในบ่อเริ่มมีกลิ่นเหม็นและขุ่นคล้ำ",
    choices: [
      {
        label: "ทนอยู่ต่อ",
        outcomes: [
          { chance: 0.5, text: "น้ำค่อยๆ ใสขึ้นหลังฝนตก", effects: { health: -5 } },
          { chance: 0.5, text: "น้ำเสียทำให้คุณป่วย", effects: { health: -30, cause: "human" } },
        ],
      },
      { label: "คลานขึ้นไปซ่อนที่กอพืชริมน้ำ", outcomes: [{ chance: 1, text: "อากาศดีกว่า แต่อาหารน้อย", effects: { fullness: -10 } }] },
    ],
  },
  {
    id: "dragonfly_aerial",
    archetypes: I,
    stages: ["adult"],
    requires: { subgroups: ["insect.dragonfly"] },
    weight: 8,
    text: "ยุงฝูงใหญ่บินวนอยู่เหนือผิวน้ำ",
    fact: "แมลงปอเป็นนักล่ากลางอากาศที่จับเหยื่อสำเร็จบ่อยมาก และกินยุงได้วันละหลายสิบตัว",
    choices: [
      { label: "บินโฉบจับกลางอากาศ", outcomes: [{ chance: 1, text: "ขาทั้งหกของคุณรวบยุงได้ทีละตัว", effects: { fullness: 35 } }] },
      {
        label: "เกาะรอบนกิ่งไม้ริมน้ำ",
        outcomes: [
          { chance: 0.7, text: "คุณพุ่งออกไปจับแล้วกลับมาเกาะที่เดิม", effects: { fullness: 25, safety: 5 } },
          { chance: 0.3, text: "นกกระเต็นเกาะอยู่กิ่งข้างๆ", effects: { safety: -20 } },
        ],
      },
    ],
  },
  {
    id: "dragonfly_patrol",
    archetypes: I,
    stages: ["adult"],
    requires: { subgroups: ["insect.dragonfly"], notFlags: ["has_mate"] },
    weight: 7,
    text: "คุณบินลาดตระเวนริมบ่อน้ำ ไล่แมลงปอตัวผู้อื่นออกจากเขต",
    choices: [
      {
        label: "ยึดเขตนี้ไว้",
        outcomes: [
          { chance: 0.65, text: "ตัวเมียบินมาวางไข่ในเขตของคุณ คุณได้คู่", effects: { addFlags: ["has_mate"], fullness: -10 } },
          { chance: 0.35, text: "การไล่กันกลางอากาศทำให้ปีกคุณขาด", effects: { health: -20, cause: "accident" } },
        ],
      },
      { label: "ไปหาบ่ออื่น", outcomes: [{ chance: 1, text: "บ่ออื่นก็มีเจ้าของแล้ว", effects: { fullness: -10 } }] },
    ],
  },

  // ---------- แมลงอื่นๆ (แมลงสาบ ตั๊กแตนตำข้าว แมลงกิ่งไม้ ฯลฯ) ----------
  {
    id: "insectother_hide",
    archetypes: I,
    stages: ["nymph", "larva", "adult"],
    requires: { subgroups: ["insect.other"] },
    weight: 6,
    text: "ตุ๊กแกตัวหนึ่งไต่ผนังลงมาหาคุณ",
    choices: [
      {
        label: "มุดเข้าซอกแคบที่สุด",
        outcomes: [
          { chance: 0.8, text: "ตัวแบนๆ ของคุณมุดเข้าซอกที่ตุ๊กแกเข้าไม่ได้", effects: { safety: 10 } },
          { chance: 0.2, text: "ซอกนั้นตื้นเกินไป", effects: { death: "predator" } },
        ],
      },
      {
        label: "ทำตัวเหมือนกิ่งไม้แห้ง",
        outcomes: [
          { chance: 0.6, text: "ตุ๊กแกมองไม่ออกว่าคุณเป็นแมลง", effects: { safety: 10 } },
          { chance: 0.4, text: "คุณขยับขาโดยไม่ตั้งใจ ตุ๊กแกงับขาข้างหนึ่งไป", effects: { health: -25, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "insectother_night",
    archetypes: I,
    stages: ["nymph", "adult"],
    requires: { subgroups: ["insect.other"] },
    weight: 7,
    text: "ความมืดมาเยือน ถึงเวลาออกหาอาหาร",
    choices: [
      { label: "ออกสำรวจไปทั่ว", outcomes: [{ chance: 1, text: "คุณเจอเศษอาหารเต็มไปหมด", effects: { fullness: 35, safety: -5 } }] },
      { label: "หากินใกล้ที่ซ่อน", outcomes: [{ chance: 1, text: "ได้น้อยแต่ปลอดภัย", effects: { fullness: 20, safety: 5 } }] },
    ],
  },
];
